// TelcoTrack — overlay open/close motion helpers (app-modal, Leaflet popup clone).
// Close must stay on-screen until the exit animation ends; reduced motion skips the wait.

export const TT_OVERLAY_EXIT_MS = 200;

export function prefersReducedMotion(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Delay before unmounting a closing overlay. 0 when the user prefers reduced motion. */
export function overlayUnmountDelayMs(): number {
  return prefersReducedMotion() ? 0 : TT_OVERLAY_EXIT_MS;
}

/**
 * Visual-only Leaflet popup exit. The live node can unmount on the same tick
 * (commit / destroy stay unchanged); the clone plays scale+fade on `document.body`.
 */
export function playOverlayExitClone(
  source: Element | null | undefined,
  fallbackRect?: Pick<DOMRect, 'width' | 'height' | 'top' | 'left'> | null,
): void {
  if (!(source instanceof HTMLElement) || prefersReducedMotion()) return;
  if (source.dataset['ttOverlayExit'] === '1') return;
  const liveRect = source.isConnected ? source.getBoundingClientRect() : null;
  const rect = liveRect && liveRect.width >= 1 && liveRect.height >= 1 ? liveRect : fallbackRect;
  if (!rect || (rect.width < 1 && rect.height < 1)) return;

  source.dataset['ttOverlayExit'] = '1';
  const clone = source.cloneNode(true) as HTMLElement;
  clone.classList.add('tt-overlay-exit-clone');
  clone.removeAttribute('id');
  clone.setAttribute('aria-hidden', 'true');
  clone.style.position = 'fixed';
  clone.style.left = `${rect.left}px`;
  clone.style.top = `${rect.top}px`;
  clone.style.width = `${rect.width}px`;
  clone.style.margin = '0';
  clone.style.transform = 'none';
  clone.style.pointerEvents = 'none';
  clone.style.zIndex = '4000';
  document.body.appendChild(clone);

  const cleanup = (): void => {
    clone.remove();
  };
  clone.addEventListener('animationend', cleanup, { once: true });
  window.setTimeout(cleanup, TT_OVERLAY_EXIT_MS + 80);
}
